import { COMPARE_WINDOW_LABEL_KEYS, conditionOptionFor } from '../models/alert.constants'
import type { AlertMetricCondition } from '../models/alert.types'
import { useAlertCellFormatter } from '../models/reports-bridge'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'

export interface AlertConditionParts {
  operator: string
  amount: string
  comparison: string
}

export interface UseAlertConditionText {
  parts: (condition: AlertMetricCondition, compareDays?: number | null) => AlertConditionParts
  text: (condition: AlertMetricCondition, compareDays?: number | null) => string
}

export function useAlertConditionText(): UseAlertConditionText {
  const { t } = useI18n()
  const format = useFormat()
  const cell = useAlertCellFormatter()

  function parts(
    condition: AlertMetricCondition,
    compareDays?: number | null,
  ): AlertConditionParts {
    const option = conditionOptionFor(condition.operator)
    const percentage = condition.mode === 'percentage'
    const amount =
      condition.amount === null
        ? cell.formatCell(null, null)
        : percentage
          ? format.formatPercent(condition.amount, { scale: 'percent' })
          : cell.formatCell(condition.amount, condition.column)
    const compareWindow = condition.compareWindow
    const comparison = compareWindow
      ? t('alerts.condition.comparison', {
          value:
            compareWindow === 'previous_n_days' && !compareDays
              ? t('alerts.compare.previousPeriod')
              : t(COMPARE_WINDOW_LABEL_KEYS[compareWindow], { days: compareDays ?? 0 }),
        })
      : ''

    return {
      operator: option ? (percentage ? option.percentSymbol : option.absSymbol) : '',
      amount,
      comparison,
    }
  }

  function text(condition: AlertMetricCondition, compareDays?: number | null): string {
    const { operator, amount, comparison } = parts(condition, compareDays)
    return [condition.column.header, operator, amount, comparison].filter(Boolean).join(' ')
  }

  return { parts, text }
}
